import { toHex, type Backdrop, type MockupJob } from '@eip/engine';
import type { PreviewMaps, PreviewResult } from '../worker/protocol';
export const previews = new Map<string, { source?: PreviewResult; output?: PreviewResult }>();
export const overlays = new Map<string, ImageBitmap>();

/**
 * Screen-print viewer bitmaps: the composite proof, each plate's preview and the
 * current 1:1 film crop, keyed `${docId}:sep:${kind}:${builtGeneration}:${filmsVersion}`
 * where `kind` is `proof`, `plate:${plateId}` or `film:${plateId}:${x}:${y}:${w}:${h}`.
 * Separate from `previews` (one source/output pair per doc) because a job has one
 * of these per plate, and separate from `overlays` because they are whole images
 * rather than masks painted over one.
 */
export const sepPreviews = new Map<string, PreviewResult>();

export function sepPreviewKey(docId: string, kind: string, builtGeneration: number, filmsVersion: number): string {
  return `${docId}:sep:${kind}:${builtGeneration}:${filmsVersion}`;
}

/** The `${builtGeneration}:${filmsVersion}` tail of a cache key. Read by splitting
 *  rather than `endsWith`, so a window coordinate inside `kind` can never be
 *  mistaken for part of the epoch. */
function epochOf(key: string): string { return key.split(':').slice(-2).join(':'); }

/**
 * Store a sep preview, closing everything the insert makes dead first: every
 * entry for this doc built at a different `builtGeneration`/`filmsVersion` (a
 * rebuild replaces the plates, a films change re-renders them, so no older
 * bitmap can be shown again), and — for a film crop — every other film crop for
 * this doc, since only one window is ever on screen and panning around a large
 * film would otherwise cache a new multi-megabyte bitmap per stop.
 */
export function putSepPreview(docId: string, kind: string, builtGeneration: number, filmsVersion: number, preview: PreviewResult): void {
  const prefix = `${docId}:sep:`;
  const epoch = `${builtGeneration}:${filmsVersion}`;
  const filmPrefix = `${prefix}film:`;
  const isFilm = kind.startsWith('film:');
  const key = sepPreviewKey(docId, kind, builtGeneration, filmsVersion);
  for (const k of [...sepPreviews.keys()]) {
    if (k === key || !k.startsWith(prefix)) continue;
    if (epochOf(k) !== epoch || (isFilm && k.startsWith(filmPrefix))) { sepPreviews.get(k)?.bitmap.close(); sepPreviews.delete(k); }
  }
  sepPreviews.get(key)?.bitmap.close();
  sepPreviews.set(key, preview);
}

/**
 * Close and evict one document's film crops, keeping its plate and proof
 * previews. A film crop is the largest bitmap the viewer holds (up to 2048² of
 * RGBA, ~16 MB), and leaving the film view means nothing will read it again
 * until the user comes back — by which time the window they return to is
 * usually a different one anyway.
 */
export function dropSepFilms(docId: string): void {
  const prefix = `${docId}:sep:film:`;
  for (const k of [...sepPreviews.keys()]) if (k.startsWith(prefix)) { sepPreviews.get(k)?.bitmap.close(); sepPreviews.delete(k); }
}

/** Close and evict every sep preview for one document. */
export function dropSepPreviews(docId: string): void {
  const prefix = `${docId}:sep:`;
  for (const k of [...sepPreviews.keys()]) if (k.startsWith(prefix)) { sepPreviews.get(k)?.bitmap.close(); sepPreviews.delete(k); }
}

/**
 * One mockup preview per document: the most recent render's bitmap (≤
 * `MOCKUP_PREVIEW_MAX` on a side) with the generation it belongs to and the
 * FULL-resolution photo size it was downscaled from, which is the coordinate
 * space the viewer's drag maths and print-area outline work in.
 *
 * A single entry rather than an epoch-keyed family (as `sepPreviews` is): a
 * mockup has no per-plate variants, every render replaces the last, and these
 * are large (1536² RGBA ≈ 9 MB) so nothing older is worth keeping.
 */
export const mockupPreviews = new Map<string, { generation: number; bitmap: ImageBitmap; width: number; height: number }>();

/** Which of a document's two mockup renders a cache entry is: its own art on its
 *  own blank (`front`), or — for a bound front+back pair — the BACK document's
 *  art on the style's back blank (`back`, `DocMockup.backJob`). Two slots rather
 *  than one entry per document, because the viewer's toggle switches between
 *  them and re-rendering on every switch would cost a second of worker time. */
export type MockupSlot = 'front' | 'back';
export function mockupPreviewKey(docId: string, slot: MockupSlot = 'front'): string {
  return slot === 'front' ? docId : `${docId}:back`;
}

/** Store a mockup preview under a `mockupPreviewKey` (a plain document id is the
 *  front slot's), closing what it replaces. A result from an OLDER generation
 *  than the one cached is refused and its bitmap closed — the runner already
 *  drops stale results, but this keeps the cache correct even if a caller does
 *  not, and never leaks the bitmap it turned away. */
export function putMockupPreview(key: string, generation: number, bitmap: ImageBitmap, width: number, height: number): void {
  const prev = mockupPreviews.get(key);
  if (prev) {
    if (prev.generation > generation) { bitmap.close(); return; }
    prev.bitmap.close();
  }
  mockupPreviews.set(key, { generation, bitmap, width, height });
}

/** Close and evict one of a document's mockup previews (both slots by default:
 *  closing a document, or clearing a pair, frees whichever it holds). */
export function dropMockupPreview(docId: string, slot?: MockupSlot): void {
  for (const s of slot ? [slot] : (['front', 'back'] as const)) {
    const key = mockupPreviewKey(docId, s);
    mockupPreviews.get(key)?.bitmap.close();
    mockupPreviews.delete(key);
  }
}

/**
 * The art-free garment a placement drag paints under its ghost, one per mockup
 * slot (`mockupPreviewKey`: a plain document id is the front). `key` is what the
 * cached bitmap actually IS (`mockupBlankKey`: the blank, the colour, and every
 * print drawn into it down to its art's generation) — so a template, colourway,
 * extra-print or re-run change is a miss rather than a wrong garment under the art.
 *
 * One entry per slot, not a family keyed by `key`: a drag needs the blank it is
 * dragging on and nothing else, and these are the same 1536² ≈ 9 MB bitmaps the
 * previews are.
 */
export const mockupBlanks = new Map<string, { key: string; bitmap: ImageBitmap; width: number; height: number }>();

/**
 * What a cached blank has to match to be usable: the blank it was rendered from
 * and the colour it was recoloured to (`'own'` — the template's own colour —
 * when the job sets none), plus everything about the job's OTHER prints that
 * changes those pixels.
 *
 * `recolorable: false` is `'own'` whatever the job asks for, because that is
 * what the compositor does with it (`compose` drops the garment for such a
 * template rather than bleeding ink colour into the backdrop): every colourway
 * of one of those blanks IS the same image, so they share one bitmap. Absent,
 * or true, means the colour counts — matching `recolorable === false` exactly,
 * which is the test the compositor makes.
 *
 * ONE function for both readers — the runner that stores a blank and the viewer
 * that looks one up — because a key they built separately could drift apart and
 * leave a drag ghosting on a garment nobody asked for. Everything either of them
 * knows is passed in: `docs` is the open documents (only `id` and the run's
 * `generation` are read) and `selfId` the document whose art a `docId: null`
 * print means — the slot's own art document, which for a bound pair's back slot
 * is the BACK document, exactly as `extraArtsFor` resolves it.
 */
/** A job's backdrop as one key fragment: `none` for the photo's own, else what it replaces it with.
 *  A scene by ID alone — its pixels cannot change under a fixed id (the store writes a fresh UUID
 *  per import), so the id names the image as exactly as a hex names a colour. */
function backdropKey(b: Backdrop | null | undefined): string {
  if (!b) return 'none';
  if (b.kind === 'color') return `color:${toHex(b.rgb)}`;
  if (b.kind === 'gradient') return `gradient:${toHex(b.top)}>${toHex(b.bottom)}`;
  return `scene:${b.sceneId}`;
}

export function mockupBlankKey(
  job: MockupJob,
  template: { recolorable?: boolean } | null | undefined,
  docs: ReadonlyArray<{ id: string; run?: { generation: number } }>,
  selfId: string | null | undefined,
): string {
  // The BACKDROP is part of the blank too, and for the same reason the garment colour is: a drag
  // ghosts over these pixels, and a blank cached before the shop swapped the backdrop would ghost
  // it over the studio sweep the render has already replaced.
  const base = `${job.templateId}:${job.garment && template?.recolorable !== false ? toHex(job.garment) : 'own'}:${backdropKey(job.backdrop)}`;
  // A blank carries everything a placement drag is NOT moving — which, since 3C-1, includes the
  // job's other prints. Two jobs whose extra prints differ are therefore two different blanks, and
  // sharing one would ghost the drag over a sleeve that is not there (or the wrong one). Only the
  // ENABLED prints: a disabled one draws nothing, so that blank is the plain one.
  const drawn = (job.extraPrints ?? []).filter((p) => p.enabled);
  if (drawn.length === 0) return base;
  // Each drawn print's ART GENERATION as well as its placement: re-running a document that one of
  // them prints (or this document itself, for a `docId: null` print) produces different pixels at
  // the same placement, and without this the next drag would keep ghosting the old art until some
  // other part of the key moved. `docId: null` and this document's own id are the same lookup, the
  // same way the compositor resolves them; a closed or never-run document is `null` — the print is
  // skipped, and the blank it produces is the one without it.
  const generationOf = (docId: string | null): number | null => {
    const id = docId ?? selfId;
    return (id ? docs.find((d) => d.id === id)?.run?.generation : undefined) ?? null;
  };
  return `${base}:${JSON.stringify(drawn.map((p) => [p.areaId, p.docId, generationOf(p.docId), p.placement.widthIn, p.placement.xIn, p.placement.yIn, p.rotationDeg]))}`;
}

/** The cached blank for this slot, but only when it is the one `key` describes. */
export function getMockupBlank(slotKey: string, key: string): { bitmap: ImageBitmap; width: number; height: number } | undefined {
  const entry = mockupBlanks.get(slotKey);
  return entry && entry.key === key ? entry : undefined;
}

/** Store a blank, closing whatever it replaces — including a bitmap for the same
 *  key, so a redundant fetch cannot leak one. */
export function putMockupBlank(slotKey: string, key: string, bitmap: ImageBitmap, width: number, height: number): void {
  mockupBlanks.get(slotKey)?.bitmap.close();
  mockupBlanks.set(slotKey, { key, bitmap, width, height });
}

/** Close and evict a document's blanks (both slots by default). */
export function dropMockupBlank(docId: string, slot?: MockupSlot): void {
  for (const s of slot ? [slot] : (['front', 'back'] as const)) {
    const key = mockupPreviewKey(docId, s);
    mockupBlanks.get(key)?.bitmap.close();
    mockupBlanks.delete(key);
  }
}

/**
 * The shading maps and downscaled photo the GPU preview renders from, one entry per TEMPLATE —
 * because that is what they describe: several documents on the same blank share one set, exactly as
 * they share one worker-side preparation.
 *
 * Not keyed by document or by job: nothing in here depends on the art, the placement or the
 * colourway (the recolour is a shader uniform). What DOES invalidate an entry is the template being
 * re-prepared, which `PreviewMaps.prepGeneration` reports — the runner compares it against the
 * template's `MockupPrepared` and re-fetches when the two disagree.
 *
 * `photo` is an ImageBitmap (the rest are plain arrays), so an entry that is replaced or dropped has
 * to be closed; everything below goes through these three functions for that reason.
 *
 * SIZE. The generation check is invalidation, not lifetime: the bound on this Map is one entry per
 * template VISITED this session, and the only eviction is `mockupRunner.unload` (a user template
 * saved or deleted). An entry is ~17 MB of typed arrays for a built-in blank (1000 × 1250) and up to
 * ~41 MB at `MOCKUP_PREVIEW_MAX`, plus the bitmap. That mirrors the worker's own `templates` map,
 * which is unbounded on exactly the same terms — the maps are cached here precisely so the GPU does
 * not pay for them again, and a template the shop has looked at once it will look at again. Nothing
 * is LEAKED: every path that displaces a `photo` closes it (replace here, drop here, and a fetch
 * whose result arrives too late closes its own in `startPreviewMaps`). A browser with no GPU path at
 * all never populates this at all — see `glPreviewUnavailable` and the hook's fetch trigger.
 */
export const previewMaps = new Map<string, PreviewMaps>();

export function getPreviewMaps(templateId: string): PreviewMaps | undefined {
  return previewMaps.get(templateId);
}

/** Store one template's maps, closing the photo of whatever they replace — unless the caller handed
 *  back the very entry already cached, which is not a replacement at all. */
export function putPreviewMaps(maps: PreviewMaps): void {
  const prev = previewMaps.get(maps.templateId);
  if (prev && prev !== maps) prev.photo.close();
  previewMaps.set(maps.templateId, maps);
}

/** Close and evict one template's maps (the template being unloaded or re-saved). */
export function dropPreviewMaps(templateId: string): void {
  previewMaps.get(templateId)?.photo.close();
  previewMaps.delete(templateId);
}

/**
 * One backdrop scene cut to cover one preview size exactly, keyed by `sceneCoverKey` — the texture
 * the GPU preview uploads and fetches pixel for pixel.
 *
 * Cut in the WORKER (`mockupSceneCover`), by the very `coverImage` the CPU compositor reads, which
 * is what makes the two renders the same image rather than two resamples of one scene. Cached here
 * for the same reason `previewMaps` is: it is a worker call and a full-photo Lanczos resample per
 * (scene, size), and neither the job, the placement nor the colourway changes it.
 *
 * SIZE. One entry per scene per preview size actually rendered — in practice one per scene, since
 * the size is the blank's — bounded by the scenes the shop has, and evicted only by
 * `dropSceneCovers` when a scene is removed or unloaded. An entry is one `ImageBitmap` at the
 * preview size (≤ 1536² ≈ 9 MB), and it mirrors the worker's own `covers` map exactly.
 */
export const sceneCovers = new Map<string, ImageBitmap>();

export function getSceneCover(key: string): ImageBitmap | undefined {
  return sceneCovers.get(key);
}

/** Store one cover, closing what it replaces — unless the caller handed back the very bitmap
 *  already cached, which is not a replacement at all. */
export function putSceneCover(key: string, bitmap: ImageBitmap): void {
  const prev = sceneCovers.get(key);
  if (prev === bitmap) return;
  prev?.close();
  sceneCovers.set(key, bitmap);
}

/** Close and evict every size of one scene's cover: the scene being deleted, or dropped from the
 *  worker — after which nothing may draw from pixels the worker no longer holds. */
export function dropSceneCovers(sceneId: string): void {
  const prefix = `${sceneId}|`;
  for (const k of [...sceneCovers.keys()]) if (k.startsWith(prefix)) { sceneCovers.get(k)?.close(); sceneCovers.delete(k); }
}

export function dropDoc(id: string) {
  const p = previews.get(id); p?.source?.bitmap.close(); p?.output?.bitmap.close(); previews.delete(id);
  for (const k of [...overlays.keys()]) if (k.startsWith(id + ':')) { overlays.get(k)?.close(); overlays.delete(k); }
  dropSepPreviews(id);
  dropMockupPreview(id);
  dropMockupBlank(id);
}

/** Store an overlay bitmap, closing and evicting any stale entry for the same doc/which/key (older version/generation) first. */
export function putOverlay(docId: string, which: string, key: string, version: string, bitmap: ImageBitmap): void {
  const prefix = `${docId}:${which}:${key}:`;
  for (const k of [...overlays.keys()]) if (k.startsWith(prefix)) { overlays.get(k)?.close(); overlays.delete(k); }
  overlays.set(`${prefix}${version}`, bitmap);
}

/** True when some version of this overlay is cached. Lets a caller skip `dropOverlay`
 *  (and its full key scan) for the overlays that were never fetched in the first place. */
export function hasOverlay(docId: string, which: string, key: string): boolean {
  const prefix = `${docId}:${which}:${key}:`;
  for (const k of overlays.keys()) if (k.startsWith(prefix)) return true;
  return false;
}

/** Close and evict every cached version of one overlay (a check that no longer reports a mask). */
export function dropOverlay(docId: string, which: string, key: string): void {
  const prefix = `${docId}:${which}:${key}:`;
  for (const k of [...overlays.keys()]) if (k.startsWith(prefix)) { overlays.get(k)?.close(); overlays.delete(k); }
}
